import {
  ExternalLinkIcon,
  FileIcon,
  FileUpIcon,
  LoaderIcon,
  MusicIcon,
  UploadIcon,
  XIcon,
} from "lucide-react";
import { ComponentProps, FC, MouseEvent, useCallback, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Link } from "react-router";
import { toast } from "sonner";

import {
  FileUploadProvider,
  useFileUpload,
} from "@/components/file-upload-context";
import { Button } from "@/components/ui/button";
import { useFormField } from "@/components/ui/form";
import { Progress } from "@/components/ui/progress";
import { FileType, UploadedFile, UploadFileOptions } from "@/lib/uploads";
import { cn } from "@/lib/utils";

type FileUploadInputProps = ComponentProps<"input"> & {
  fileType: FileType;
  removeDisabled?: boolean;
  previewDisabled?: boolean;
  onUploadFile?: (
    options: UploadFileOptions,
  ) => Promise<UploadedFile | undefined>;
  onRemoveFile?: () => Promise<unknown>;
  minWidth?: number;
  maxWidth?: number;
  maxHeight?: number;
  maxFileSize?: number;
};

type FileUploadInputInnerProps = {
  value: string;
  fileType: FileType;
  removeDisabled: boolean;
  previewDisabled: boolean;
  onRemoveFile?: () => Promise<unknown>;
};

// TODO: Make this more generic
// Switch based on mime type + some parameters like width and height.
type AssetPreviewProps = {
  value: string;
  fileType: FileType;
};

// TODO: Refactor this component to use a context and ensure that unused props
// are not passed to inner components.

export const FileUploadInput: FC<FileUploadInputProps> = ({
  name,
  value,
  fileType,
  removeDisabled = false,
  previewDisabled = false,
  maxFileSize,
  minWidth,
  maxWidth,
  maxHeight,
  onUploadFile,
  onRemoveFile,
  accept,
}) => {
  const { setValue } = useFormContext();

  const handleUploadFinish = useCallback(
    (files: UploadedFile[]) => {
      setValue(name as string, files[0].url, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [setValue, name],
  );

  return (
    <FileUploadProvider
      onFinish={handleUploadFinish}
      maxFileSize={maxFileSize}
      minWidth={minWidth}
      maxWidth={maxWidth}
      maxHeight={maxHeight}
      onUploadFile={onUploadFile}
      accept={accept}
    >
      <div className="min-w-55">
        <FileUploadInputInner
          fileType={fileType}
          value={value as string}
          removeDisabled={removeDisabled}
          previewDisabled={previewDisabled}
          onRemoveFile={onRemoveFile}
        />
      </div>
    </FileUploadProvider>
  );
};

const FileUploadInputInner: FC<FileUploadInputInnerProps> = ({
  value,
  fileType,
  removeDisabled,
  previewDisabled,
  onRemoveFile,
}) => {
  const { isUploading, openFilePicker, uploadProgress } = useFileUpload();

  const handleUploadButtonClick = useCallback(() => {
    openFilePicker(fileType);
  }, [openFilePicker, fileType]);

  if (value) {
    return (
      <UploadedAsset
        value={value}
        fileType={fileType}
        removeDisabled={removeDisabled}
        previewDisabled={previewDisabled}
        onRemoveFile={onRemoveFile}
      />
    );
  }

  if (isUploading) {
    return (
      <Button variant="secondary" disabled className="w-[146px]" type="button">
        <Progress value={uploadProgress} />
        <span className="w-[48px] text-right">{uploadProgress}%</span>
      </Button>
    );
  }

  return (
    <Button
      variant="secondary"
      onClick={handleUploadButtonClick}
      className="w-[146px]"
      type="button"
    >
      <UploadIcon /> Upload file
    </Button>
  );
};

export const UploadedAsset: FC<FileUploadInputInnerProps> = ({
  value,
  fileType,
  removeDisabled,
  previewDisabled,
  onRemoveFile,
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const { isUploading, openFilePicker } = useFileUpload();
  const { setValue } = useFormContext();
  const { name } = useFormField();

  const handleDelete = useCallback(async () => {
    try {
      setIsDeleting(true);
      await onRemoveFile?.();
      setValue(name, "", { shouldDirty: true, shouldValidate: true });
    } catch (error) {
      toast.error("Failed to remove uploaded file", {
        description: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      setIsDeleting(false);
    }
  }, [setValue, name, onRemoveFile]);

  const handlePreviewButtonClick = useCallback((event: MouseEvent) => {
    event.stopPropagation();
  }, []);

  return (
    <div className="p-3 border flex flex-1 rounded-lg gap-4 justify-between items-center bg-gray-50 min-h-19 min-w-0">
      <div
        className={cn(
          "flex flex-1 min-w-0 gap-2 items-center",
          isUploading ? "opacity-50" : "",
        )}
      >
        <AssetPreview value={value} fileType={fileType} />
        <p className="font-medium text-primary text-sm truncate">
          {isUploading ? "Uploading file" : value}
        </p>
      </div>
      <div className="flex min-w-0 whitespace-nowrap">
        <Button
          variant="ghost"
          type="button"
          onClick={() => openFilePicker(fileType)}
          disabled={isUploading}
        >
          <FileUpIcon />
        </Button>
        {!previewDisabled && (
          <Button
            variant="ghost"
            onClick={handlePreviewButtonClick}
            className="px-3"
            disabled={isUploading}
            type="button"
          >
            <Link
              to={(value as string)?.replace("/manifest/video.m3u8", "/watch")}
              target="_blank"
            >
              <ExternalLinkIcon />
            </Link>
          </Button>
        )}
        {!removeDisabled && (
          <Button
            type="button"
            variant="ghost"
            onClick={handleDelete}
            disabled={isUploading || isDeleting}
          >
            <XIcon />
          </Button>
        )}
      </div>
    </div>
  );
};

const AssetPreview: FC<AssetPreviewProps> = ({ value, fileType }) => {
  const { name } = useFormField();
  const { isUploading } = useFileUpload();

  if (isUploading) {
    return (
      <div className="flex justify-center items-center size-13 min-w-13 rounded-md bg-gray-200 animate-pulse">
        <LoaderIcon className="size-4 text-secondary-foreground" />
      </div>
    );
  }

  if (name === "header_logo_url") {
    return (
      <img
        src={value.replace("public", "400")}
        alt="Asset Preview"
        className="max-h-13 object-contain"
      />
    );
  }

  if (fileType === FileType.IMAGE) {
    return (
      <img
        src={value.replace("public", "400")}
        alt="Asset Preview"
        className="size-13 min-w-13 rounded-md object-cover"
      />
    );
  }

  if (fileType === FileType.VIDEO) {
    return (
      <img
        src={value.replace("/manifest/video.m3u8", "/thumbnails/thumbnail.jpg")}
        alt="Asset Preview"
        className="size-13 min-w-13 rounded-md object-cover"
      />
    );
  }

  if (fileType === FileType.AUDIO) {
    return (
      <div className="size-13 min-w-13 bg-purple-50 rounded-sm flex justify-center items-center">
        <MusicIcon className="text-purple-500" />
      </div>
    );
  }

  return (
    <div className="size-13 min-w-13 bg-green-400 rounded-sm flex justify-center items-center">
      <FileIcon className="text-white" />
    </div>
  );
};
